"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CircleCheck as CheckCircle2, ShieldCheck, Star, Clock, Wrench } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HeroHomeMaintenance() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imgRef,
    isVisible: isImgVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const heroImage = {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/home_maintenance_hero.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/home_maintenance_hero.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/home_maintenance_hero.webp-wvc-sizes",
    alt: "Professional handyman performing home maintenance and repairs in Atlanta"
  };
  return <section id="herohomemaintenance" className="relative bg-background overflow-hidden py-16 md:py-24 lg:py-32">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          {}
          <div ref={textRef} className={`flex flex-col items-start space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Badge variant="secondary" className="px-3 py-1.5 text-sm font-medium">
              <Wrench className="w-4 h-4 mr-1.5 text-primary" />
              Atlanta Home Maintenance Pros
            </Badge>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-foreground leading-[1.1] tracking-tight">
              Atlanta Home Maintenance — One Call for All Your Repairs
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground font-medium flex flex-wrap items-center gap-x-3 gap-y-2">
              <span>Independent local pros</span>
              <span className="text-primary/40 hidden sm:inline">|</span>
              <span>Preventative care</span>
              <span className="text-primary/40 hidden sm:inline">|</span>
              <span>Reliable service</span>
            </p>

            <div className="w-full space-y-3 pt-2">
              <h3 className="text-lg font-semibold text-foreground">Our Services</h3>
              <div className="flex flex-wrap gap-2 text-sm">
                <Link to="/PlumbingServicesPage" className="px-3 py-1.5 rounded-lg bg-card border border-border text-foreground hover:bg-accent transition-colors">
                  Plumbing Services
                </Link>
                <Link to="/ElectricalServicesPage" className="px-3 py-1.5 rounded-lg bg-card border border-border text-foreground hover:bg-accent transition-colors">
                  Electrical Services
                </Link>
                <Link to="/HVACServicesPage" className="px-3 py-1.5 rounded-lg bg-card border border-border text-foreground hover:bg-accent transition-colors">
                  HVAC Services
                </Link>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 w-full sm:w-auto pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto text-base h-14 px-8 shadow-md">
                <Link to="#bookingrequest">Request a Quote</Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 border-t border-border/60 w-full">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="w-5 h-5 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">Vetted Local Pros</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Clock className="w-5 h-5 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">Flexible Scheduling</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">Quotes Before Work Starts</span>
              </div>
            </div>
          </div>

          {}
          <div ref={imgRef} className={`relative lg:ml-auto w-full max-w-lg lg:max-w-none mx-auto transition-all duration-1000 delay-200 ease-out ${isImgVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3] lg:aspect-[4/5] xl:aspect-square border border-border/50">
              <img src={heroImage.src} data-wvc-srcset={heroImage.srcset} data-wvc-sizes={heroImage.sizes} alt={heroImage.alt} className="w-full h-full object-cover" loading="eager" />
              {}
              <div className="absolute inset-0 bg-gradient-to-tr from-black/10 to-transparent pointer-events-none" />
            </div>

            {}
            <div className="absolute -bottom-6 -left-6 sm:-left-8 bg-card p-4 sm:p-5 rounded-xl shadow-xl border border-border flex items-center gap-4 z-20">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm sm:text-base font-bold text-foreground font-default leading-tight">Upfront Quotes</p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Confirm before work begins</p>
              </div>
            </div>
            
            {}
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-primary/10 rounded-lg blur-3xl -z-10 pointer-events-none" />
          </div>

        </div>
      </div>
    </section>;
}